import React from 'react';
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Group,
  Title,
  Text,
  Card,
  Alert,
  Center
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconLogin, IconInfoCircle } from '@tabler/icons-react';

interface UnirseSalaModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (codigoSala: string) => void;
  loading: boolean;
}

const UnirseSalaModal: React.FC<UnirseSalaModalProps> = ({
  opened,
  onClose,
  onSubmit,
  loading
}) => {
  const form = useForm({
    initialValues: {
      codigoSala: ''
    },
    validate: {
      codigoSala: (value: string) => {
        if (!value || value.trim().length === 0) {
          return 'Ingresa el código de la sala';
        }
        if (value.trim().length !== 6) {
          return 'El código debe tener exactamente 6 caracteres';
        }
        if (!/^[A-Z0-9]+$/.test(value.trim().toUpperCase())) {
          return 'El código solo puede contener letras y números';
        }
        return null;
      }
    }
  });

  const handleSubmit = (values: typeof form.values) => {
    const codigoSala = values.codigoSala.trim().toUpperCase();
    onSubmit(codigoSala);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    form.setFieldValue('codigoSala', value.slice(0, 6));
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <IconLogin size={24} className="text-green-500" />
          <Title order={3}>Unirse a Sala</Title>
        </div>
      }
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          {/* Instrucciones */}
          <Card withBorder padding="sm" className="bg-green-50">
            <Alert 
              icon={<IconInfoCircle size={16} />}
              color="green"
              variant="light"
            >
              Ingresa el código de <strong>6 caracteres</strong> que te proporcionó el creador de la sala.
            </Alert>
          </Card>

          <Center>
            <Stack gap="md" align="center">
              <Text size="sm" color="dimmed" ta="center">
                El código se ve así:
              </Text>
              <Card withBorder padding="xs" className="bg-gray-50">
                <Text 
                  size="xl" 
                  fw={700} 
                  ta="center" 
                  className="font-mono tracking-widest text-gray-600"
                >
                  ABC123
                </Text>
              </Card>
            </Stack>
          </Center>

          {/* Input del código */}
          <TextInput
            label="Código de Sala"
            placeholder="Ej: ABC123"
            description="Ingresa el código exactamente como te lo dieron"
            size="lg"
            className="text-center"
            styles={{
              input: {
                fontSize: '1.5rem',
                fontWeight: 600,
                textAlign: 'center',
                letterSpacing: '0.2em',
                fontFamily: 'monospace',
                textTransform: 'uppercase'
              }
            }}
            value={form.values.codigoSala}
            onChange={handleInputChange}
            error={form.errors.codigoSala}
            required
            maxLength={6}
          />

          {/* Botones */}
          <Group justify="flex-end" gap="md" mt="lg">
            <Button
              variant="subtle"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={loading}
              variant="gradient"
              gradient={{ from: 'green', to: 'lime' }}
              disabled={form.values.codigoSala.trim().length !== 6}
            >
              Unirse a Sala
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default UnirseSalaModal;